import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getFirestore, collection, query, getDocs, setDoc, doc, Timestamp, updateDoc } from 'firebase/firestore';
import { createUserWithEmailAndPassword, getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { initializeApp, getApps } from 'firebase/app';
import { app, firebaseConfig } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import swal from 'sweetalert';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBoxArchive,
  faBuilding,
  faEye,
  faPenToSquare,
  faPlus,
  faRotateLeft,
  faUserCheck,
  faUsers
} from '@fortawesome/free-solid-svg-icons';
import '../dash.css';
import { ORGANIZATION_NAME } from '../../branding';
import { buildEmployeeAccountEmail } from '../../utils/emailTemplates';

const ENGAGEMENT_OPTIONS = [
  'Permanent',
  'Contract',
  'Internship',
  'On Job Training',
  'Attachment',
  'Government Attachee',
  'Researcher',
  'Volunteer'
];

const TAX_OPTIONS = ['PAYEE', 'Consulatant'];

const GENDER_OPTIONS = ['Male', 'Female', 'Other'];

const JOB_OPTIONS = [
  { value: '', label: 'Select Job Position' },
  { value: 'Instructor', label: 'Instructor' },
  { value: 'Administrator', label: 'Administrator' },
  { value: 'Front Office', label: 'Front Office' },
  { value: 'Accountant', label: 'Accountant' },
  { value: 'Admissions', label: 'Admission Assistant' },
  { value: 'Lawyer', label: 'Lawyer' },
  { value: 'Marketing', label: 'Marketing' },
  { value: 'Therapist', label: 'Therapist' },
  { value: 'Other', label: 'Other' }
];

const COUNTRY_OPTIONS = ['Kenya', 'Uganda', 'Rwanda', 'Tanzania', 'Ethiopia'];

const buildEmptyEmployeeForm = () => ({
  campus: '',
  employeeName: '',
  employeePhone: '',
  engagementType: ENGAGEMENT_OPTIONS[0],
  taxType: TAX_OPTIONS[0],
  addEmployeeGender: GENDER_OPTIONS[0],
  jobPosition: '',
  country: COUNTRY_OPTIONS[0],
  employeeID: '',
  addEmployeeAge: '',
  employeeStartDate: '',
  salary: '',
  currentEmail: '',
  employeeEmail: '',
  password: '',
  employeeStatus: 'Active'
});

function EmployeeComp() {
  const db = getFirestore(app);
  const auth = getAuth();
  const navigate = useNavigate();
  const secondaryApp = (() => {
    const existing = getApps().find((firebaseApp) => firebaseApp.name === 'secondary');
    return existing || initializeApp(firebaseConfig, 'secondary');
  })();
  const secondaryAuth = getAuth(secondaryApp);

  const [employees, setEmployees] = useState([]);
  const [branchesList, setBranchesList] = useState([]);
  const [modalShow, setModalShow] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [token, setToken] = useState('');
  const [formMode, setFormMode] = useState('add');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeForm, setEmployeeForm] = useState(buildEmptyEmployeeForm());

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        user.getIdToken().then(setToken).catch((error) => console.error(error));
      }
    });
    return () => unsubscribe();
  }, [auth]);

  const fetchEmployees = useCallback(async () => {
    const querySnapshot = await getDocs(query(collection(db, 'Employees')));
    const records = querySnapshot.docs.map((employeeDoc) => ({
      id: employeeDoc.id,
      ...employeeDoc.data()
    }));

    records.sort((first, second) => {
      const firstTime = first.timeCreated?.seconds || 0;
      const secondTime = second.timeCreated?.seconds || 0;
      return secondTime - firstTime;
    });

    setEmployees(records);
  }, [db]);

  const fetchBranches = useCallback(async () => {
    const querySnapshot = await getDocs(query(collection(db, 'Branches')));
    const branches = querySnapshot.docs.map((branchDoc) => ({
      id: branchDoc.id,
      ...branchDoc.data()
    }));
    setBranchesList(branches);
  }, [db]);

  useEffect(() => {
    const loadPage = async () => {
      try {
        setLoading(true);
        await Promise.all([fetchBranches(), fetchEmployees()]);
      } finally {
        setLoading(false);
      }
    };

    loadPage();
  }, [fetchBranches, fetchEmployees]);

  const availableCampuses = useMemo(() => {
    const branchNames = branchesList.map((branch) => branch.branchName).filter(Boolean);
    if (!branchNames.includes('General')) {
      branchNames.push('General');
    }
    return branchNames;
  }, [branchesList]);

  useEffect(() => {
    if (availableCampuses.length === 0) return;

    setEmployeeForm((currentForm) => (
      currentForm.campus
        ? currentForm
        : { ...currentForm, campus: availableCampuses[0] }
    ));
  }, [availableCampuses]);

  const statusSummary = useMemo(() => {
    return employees.reduce((summary, employee) => {
      const status = String(employee.employeeStatus || 'Active').trim();
      summary.total += 1;
      summary.totalSalary += parseFloat(employee.salary || 0) || 0;
      if (status === 'Archived') summary.archived += 1;
      else if (status === 'Inactive') summary.inactive += 1;
      else summary.active += 1;
      return summary;
    }, {
      total: 0,
      active: 0,
      inactive: 0,
      archived: 0,
      totalSalary: 0
    });
  }, [employees]);

  const filteredEmployees = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return employees.filter((employee) => {
      const employeeStatus = String(employee.employeeStatus || 'Active').trim();
      const matchesStatus = statusFilter === 'All' || employeeStatus === statusFilter;

      if (!matchesStatus) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const searchBlob = [
        employee.employeeName,
        employee.employeePhone,
        employee.employeeEmail,
        employee.currentEmail,
        employee.jobPosition,
        employee.campus,
        employee.employeeID
      ].join(' ').toLowerCase();

      return searchBlob.includes(normalizedSearch);
    }).sort((first, second) => {
      const firstSalary = parseFloat(first.salary || 0) || 0;
      const secondSalary = parseFloat(second.salary || 0) || 0;
      return secondSalary - firstSalary;
    });
  }, [employees, searchTerm, statusFilter]);

  const openAddModal = () => {
    setFormMode('add');
    setSelectedEmployee(null);
    setEmployeeForm({
      ...buildEmptyEmployeeForm(),
      campus: availableCampuses[0] || 'General'
    });
    setModalShow(true);
  };

  const openEditModal = (employee) => {
    setFormMode('edit');
    setSelectedEmployee(employee);
    setEmployeeForm({
      campus: employee.campus || availableCampuses[0] || 'General',
      employeeName: employee.employeeName || '',
      employeePhone: employee.employeePhone || '',
      engagementType: employee.engagementType || ENGAGEMENT_OPTIONS[0],
      taxType: employee.taxType || TAX_OPTIONS[0],
      addEmployeeGender: employee.addEmployeeGender || GENDER_OPTIONS[0],
      jobPosition: employee.jobPosition || '',
      country: employee.country || COUNTRY_OPTIONS[0],
      employeeID: employee.employeeID || '',
      addEmployeeAge: employee.addEmployeeAge || '',
      employeeStartDate: employee.employeeStartDate || '',
      salary: employee.salary || '',
      currentEmail: employee.currentEmail || '',
      employeeEmail: employee.employeeEmail || '',
      password: '',
      employeeStatus: employee.employeeStatus || 'Active'
    });
    setModalShow(true);
  };

  const closeModal = () => {
    if (submitting) return;
    setModalShow(false);
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setEmployeeForm((currentForm) => ({
      ...currentForm,
      [name]: value
    }));
  };

  const validateEmployeeForm = () => {
    const requiredFields = [
      ['campus', 'Campus'],
      ['employeeName', 'Employee name'],
      ['employeePhone', 'Phone number'],
      ['jobPosition', 'Job position'],
      ['salary', 'Salary'],
      ['currentEmail', 'Current primary email'],
      ['employeeID', 'Passport or ID number'],
      ['addEmployeeAge', 'Date of birth'],
      ['employeeStartDate', 'Employee start date']
    ];

    for (const [field, label] of requiredFields) {
      if (!String(employeeForm[field] || '').trim()) {
        swal('Missing Information', `${label} is required.`, 'error');
        return false;
      }
    }

    if (formMode === 'add') {
      if (!String(employeeForm.employeeEmail || '').trim()) {
        swal('Missing Information', `${ORGANIZATION_NAME} email is required.`, 'error');
        return false;
      }

      if (!String(employeeForm.password || '').trim()) {
        swal('Missing Information', 'Password is required when creating a new employee.', 'error');
        return false;
      }
    }

    return true;
  };

  const buildEmployeePayload = () => ({
    employeeName: employeeForm.employeeName.trim(),
    employeePhone: employeeForm.employeePhone.trim(),
    engagementType: employeeForm.engagementType,
    jobPosition: employeeForm.jobPosition,
    salary: employeeForm.salary,
    campus: employeeForm.campus,
    employeeEmail: employeeForm.employeeEmail.trim(),
    currentEmail: employeeForm.currentEmail.trim(),
    addEmployeeGender: employeeForm.addEmployeeGender,
    employeeID: employeeForm.employeeID.trim(),
    addEmployeeAge: employeeForm.addEmployeeAge,
    taxType: employeeForm.taxType,
    employeeStartDate: employeeForm.employeeStartDate,
    country: employeeForm.country,
    userType: employeeForm.jobPosition,
    employeeStatus: employeeForm.employeeStatus || 'Active'
  });

  const persistNewEmployee = async () => {
    const userCredential = await createUserWithEmailAndPassword(
      secondaryAuth,
      employeeForm.employeeEmail.trim(),
      employeeForm.password
    );
    const userId = userCredential.user.uid;
    await signOut(secondaryAuth);

    const payload = {
      ...buildEmployeePayload(),
      docId: userId,
      timeCreated: Timestamp.now(),
      archivedAt: null,
      updatedAt: Timestamp.now()
    };

    await setDoc(doc(db, 'Employees', userId), payload);

    const accountType = employeeForm.jobPosition === 'Instructor' ? 'Instructor' : 'Staff';
    const accountLink = employeeForm.jobPosition === 'Instructor'
      ? 'https://developer.ubunifucollege.com/'
      : 'https://meet.ubunifucollege.com/';

    await sendEmployeeWelcome({
      name: employeeForm.employeeName.trim(),
      accountType,
      accountLink,
      currentEmail: employeeForm.currentEmail.trim(),
      email: employeeForm.employeeEmail.trim(),
      password: employeeForm.password,
      phone: employeeForm.employeePhone.trim()
    });

    return {
      id: userId,
      ...payload
    };
  };

  const persistEmployeeEdit = async () => {
    if (!selectedEmployee?.id) return null;

    const payload = {
      ...buildEmployeePayload(),
      employeeEmail: selectedEmployee.employeeEmail || employeeForm.employeeEmail.trim(),
      updatedAt: Timestamp.now()
    };

    await updateDoc(doc(db, 'Employees', selectedEmployee.id), payload);

    return {
      ...selectedEmployee,
      ...payload
    };
  };

  const handleSaveEmployee = async () => {
    if (!validateEmployeeForm()) return;

    setSubmitting(true);
    try {
      if (formMode === 'add') {
        const createdEmployee = await persistNewEmployee();
        setEmployees((currentEmployees) => [createdEmployee, ...currentEmployees]);
        swal('Success', 'Employee added successfully.', 'success');
      } else {
        const updatedEmployee = await persistEmployeeEdit();
        setEmployees((currentEmployees) => currentEmployees.map((employee) => (
          employee.id === updatedEmployee.id ? updatedEmployee : employee
        )));
        swal('Updated', 'Employee details updated successfully.', 'success');
      }

      setModalShow(false);
    } catch (error) {
      console.error(error);
      swal('Error', error.message || 'Something went wrong while saving the employee.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleArchiveEmployee = async (employee) => {
    const confirmed = window.confirm(`Archive ${employee.employeeName || 'this employee'}? You can restore them later.`);
    if (!confirmed) return;

    try {
      await updateDoc(doc(db, 'Employees', employee.id), {
        employeeStatus: 'Archived',
        archivedAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });

      setEmployees((currentEmployees) => currentEmployees.map((currentEmployee) => (
        currentEmployee.id === employee.id
          ? { ...currentEmployee, employeeStatus: 'Archived', archivedAt: Timestamp.now() }
          : currentEmployee
      )));

      swal('Archived', 'Employee archived successfully.', 'success');
    } catch (error) {
      console.error('Error archiving employee:', error);
      swal('Error', 'Failed to archive employee.', 'error');
    }
  };

  const handleRestoreEmployee = async (employee) => {
    try {
      await updateDoc(doc(db, 'Employees', employee.id), {
        employeeStatus: 'Active',
        archivedAt: null,
        updatedAt: Timestamp.now()
      });

      setEmployees((currentEmployees) => currentEmployees.map((currentEmployee) => (
        currentEmployee.id === employee.id
          ? { ...currentEmployee, employeeStatus: 'Active', archivedAt: null }
          : currentEmployee
      )));

      swal('Restored', 'Employee restored to active status.', 'success');
    } catch (error) {
      console.error('Error restoring employee:', error);
      swal('Error', 'Failed to restore employee.', 'error');
    }
  };

  const sendEmployeeWelcome = async ({ name, accountType, accountLink, currentEmail, email, password, phone }) => {
    const message = `Hey ${name}, your ${accountType} account for ${ORGANIZATION_NAME} has been created successfully. Your email is ${email} and your password is ${password}. Access your account here: ${accountLink}`;
    const emailMessage = buildEmployeeAccountEmail({
      name,
      accountType,
      accountLink,
      email,
      password
    });
    const emailSubject = `${accountType} Account Registration - ${ORGANIZATION_NAME}`;

    if (phone) {
      try {
        await fetch('https://us-central1-ubunifu-college.cloudfunctions.net/api/send-sms', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            phoneNumber: phone,
            message
          })
        });
      } catch (error) {
        console.error('Error sending employee SMS:', error);
      }
    }

    if (currentEmail) {
      try {
        await fetch('https://us-central1-ubunifu-college.cloudfunctions.net/api/send-email', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            email: currentEmail,
            message: emailMessage,
            subject: emailSubject
          })
        });
      } catch (error) {
        console.error('Failed to send employee email:', error);
      }
    }
  };

  const getStatusClassName = (status) => String(status || 'Active').trim().toLowerCase();

  const formatMoney = (value) => {
    const numericValue = parseFloat(value || 0);
    if (!Number.isFinite(numericValue)) return 'KES 0';
    return `KES ${numericValue.toLocaleString('en-KE')}`;
  };

  return (
    <div className="employeeDirectoryPage">
      <div className="employeeDirectoryHero">
        <div className="employeeDirectoryHeroCopy">
          <p className="employeeDirectoryEyebrow">HR operations</p>
          <h3>Employees</h3>
          <p>
            Manage active staff records, update employee details, and archive people safely
            without deleting employment history from the platform.
          </p>
          <div className="employeeDirectoryHeroMeta">
            <span>Edit details</span>
            <span>Archive instead of delete</span>
            <span>Restore when needed</span>
          </div>
        </div>
        <div className="employeeDirectoryHeroActions">
          <button type="button" className="employeeDirectoryPrimaryBtn" onClick={openAddModal}>
            <FontAwesomeIcon icon={faPlus} />
            <span>Add Employee</span>
          </button>
        </div>
      </div>

      <div className="employeeDirectoryStats">
        <div className="employeeDirectoryStatCard">
          <span className="employeeDirectoryStatIcon"><FontAwesomeIcon icon={faUsers} /></span>
          <p>Total employees</p>
          <strong>{statusSummary.total}</strong>
        </div>
        <div className="employeeDirectoryStatCard">
          <span className="employeeDirectoryStatIcon"><FontAwesomeIcon icon={faUserCheck} /></span>
          <p>Active</p>
          <strong>{statusSummary.active}</strong>
        </div>
        <div className="employeeDirectoryStatCard">
          <span className="employeeDirectoryStatIcon"><FontAwesomeIcon icon={faUsers} /></span>
          <p>Inactive</p>
          <strong>{statusSummary.inactive}</strong>
        </div>
        <div className="employeeDirectoryStatCard">
          <span className="employeeDirectoryStatIcon"><FontAwesomeIcon icon={faBoxArchive} /></span>
          <p>Archived</p>
          <strong>{statusSummary.archived}</strong>
        </div>
        <div className="employeeDirectoryStatCard">
          <span className="employeeDirectoryStatIcon"><FontAwesomeIcon icon={faBuilding} /></span>
          <p>Total salary</p>
          <strong>{formatMoney(statusSummary.totalSalary)}</strong>
        </div>
      </div>

      <div className="employeeDirectoryPanel">
        <div className="employeeDirectoryToolbar">
          <div>
            <p className="employeeDirectoryEyebrow">Directory</p>
            <h4>Employee records</h4>
            <p className="employeeDirectoryToolbarMeta">
              Showing {filteredEmployees.length} of {statusSummary.total} employees.
            </p>
          </div>
          <div className="employeeDirectoryFilters">
            <Form.Control
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search name, email, phone, role, campus..."
            />
            <Form.Select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              <option value="All">All statuses</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
              <option value="Archived">Archived</option>
            </Form.Select>
          </div>
        </div>

        <div className="employeeDirectoryTableWrap">
          <Table hover className="employeeDirectoryTable">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Position</th>
                <th>Engagement</th>
                <th>Salary</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="employeeDirectoryEmptyState">
                    Loading employees...
                  </td>
                </tr>
              ) : filteredEmployees.length === 0 ? (
                <tr>
                  <td colSpan="6" className="employeeDirectoryEmptyState">
                    No employees match your current filters.
                  </td>
                </tr>
              ) : (
                filteredEmployees.map((employee) => {
                  const statusSlug = getStatusClassName(employee.employeeStatus);

                  return (
                    <tr key={employee.id}>
                      <td>
                        <div className="employeeDirectoryIdentity">
                          <div className="employeeDirectoryAvatar">
                            {(employee.employeeName || 'E').trim().charAt(0).toUpperCase()}
                          </div>
                          <div className="employeeDirectoryIdentityMeta">
                            <strong>{employee.employeeName || 'Employee'}</strong>
                            <span>{employee.employeeEmail || employee.currentEmail || 'Email not set'}</span>
                            <small>{employee.employeePhone || 'Phone not set'}</small>
                          </div>
                        </div>
                      </td>
                      <td>{employee.jobPosition || '—'}</td>
                      <td>{employee.engagementType || '—'}</td>
                      <td>{formatMoney(employee.salary)}</td>
                      <td>
                        <span className={`employeeStatusBadge ${statusSlug}`}>
                          {employee.employeeStatus || 'Active'}
                        </span>
                      </td>
                      <td>
                        <div className="employeeDirectoryActions">
                          <Button variant="outline-dark" size="sm" onClick={() => navigate(`/view-employee/${employee.id}`)}>
                            <FontAwesomeIcon icon={faEye} />
                            <span>View</span>
                          </Button>
                          <Button variant="outline-secondary" size="sm" onClick={() => openEditModal(employee)}>
                            <FontAwesomeIcon icon={faPenToSquare} />
                            <span>Edit</span>
                          </Button>
                          {employee.employeeStatus === 'Archived' ? (
                            <Button variant="outline-success" size="sm" onClick={() => handleRestoreEmployee(employee)}>
                              <FontAwesomeIcon icon={faRotateLeft} />
                              <span>Restore</span>
                            </Button>
                          ) : (
                            <Button variant="outline-warning" size="sm" onClick={() => handleArchiveEmployee(employee)}>
                              <FontAwesomeIcon icon={faBoxArchive} />
                              <span>Archive</span>
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </div>
      </div>

      <Modal show={modalShow} onHide={closeModal} centered size="lg">
        <Modal.Header closeButton={!submitting}>
          <Modal.Title>{formMode === 'add' ? 'Add Employee' : 'Edit Employee'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="employeeModalIntro">
            <p>
              {formMode === 'add'
                ? 'Create a new employee record and send account details to the employee.'
                : 'Update the employee profile details stored in the admin platform.'}
            </p>
            {formMode === 'edit' && selectedEmployee?.employeeEmail && (
              <small>
                {`${ORGANIZATION_NAME} login email is shown for reference. This edit form updates the employee record only.`}
              </small>
            )}
          </div>
          <div className="employeeModalGrid">
            <Form.Group className="mb-3">
              <Form.Label>Campus</Form.Label>
              <Form.Select name="campus" value={employeeForm.campus} onChange={handleFormChange}>
                {availableCampuses.map((campus) => (
                  <option key={campus} value={campus}>{campus}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Employee Name</Form.Label>
              <Form.Control name="employeeName" type="text" value={employeeForm.employeeName} onChange={handleFormChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Phone Number</Form.Label>
              <Form.Control name="employeePhone" type="text" value={employeeForm.employeePhone} onChange={handleFormChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Engagement Type</Form.Label>
              <Form.Select name="engagementType" value={employeeForm.engagementType} onChange={handleFormChange}>
                {ENGAGEMENT_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Tax Type</Form.Label>
              <Form.Select name="taxType" value={employeeForm.taxType} onChange={handleFormChange}>
                {TAX_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Gender</Form.Label>
              <Form.Select name="addEmployeeGender" value={employeeForm.addEmployeeGender} onChange={handleFormChange}>
                {GENDER_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Job Position</Form.Label>
              <Form.Select name="jobPosition" value={employeeForm.jobPosition} onChange={handleFormChange}>
                {JOB_OPTIONS.map((option) => (
                  <option key={option.value || option.label} value={option.value}>{option.label}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Country</Form.Label>
              <Form.Select name="country" value={employeeForm.country} onChange={handleFormChange}>
                {COUNTRY_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Passport or ID Number</Form.Label>
              <Form.Control name="employeeID" type="text" value={employeeForm.employeeID} onChange={handleFormChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Date of Birth</Form.Label>
              <Form.Control name="addEmployeeAge" type="date" value={employeeForm.addEmployeeAge} onChange={handleFormChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Employee Start Date</Form.Label>
              <Form.Control name="employeeStartDate" type="date" value={employeeForm.employeeStartDate} onChange={handleFormChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Salary</Form.Label>
              <Form.Control name="salary" type="number" value={employeeForm.salary} onChange={handleFormChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Current Primary Email</Form.Label>
              <Form.Control name="currentEmail" type="email" value={employeeForm.currentEmail} onChange={handleFormChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{`${ORGANIZATION_NAME} Email`}</Form.Label>
              <Form.Control
                name="employeeEmail"
                type="email"
                value={employeeForm.employeeEmail}
                onChange={handleFormChange}
                disabled={formMode === 'edit'}
              />
            </Form.Group>

            {formMode === 'add' && (
              <Form.Group className="mb-3">
                <Form.Label>Password</Form.Label>
                <Form.Control name="password" type="password" value={employeeForm.password} onChange={handleFormChange} />
              </Form.Group>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal} disabled={submitting}>Close</Button>
          <Button variant="dark" onClick={handleSaveEmployee} disabled={submitting}>
            {submitting ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                <span className="ms-2">{formMode === 'add' ? 'Saving...' : 'Updating...'}</span>
              </>
            ) : (
              formMode === 'add' ? 'Save Employee' : 'Update Employee'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default EmployeeComp;
